import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { getPackages, createCheckout } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";

const FEATURES = [
  "Unlimited medication analyses (labs · diet · interactions · organs)",
  "Personalised email reminders — daily, weekly, or monthly",
  "Diet, lab and herbal suggestions tailored to your meds",
  "Saved history of every analysis",
  "Cancel anytime",
];

export default function PricingPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { getPackages().then(setPackages).catch(() => {}); }, []);

  const buy = async (pkgId) => {
    if (!user) {
      // Remember the user's choice so we can resume Stripe checkout after sign-in
      try { localStorage.setItem("pending_checkout", pkgId); } catch {}
      setAuthOpen(true);
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckout(pkgId);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  // After Google sign-in, if we had a pending checkout, resume it automatically
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try { pending = localStorage.getItem("pending_checkout"); } catch {}
    if (!pending) return;
    try { localStorage.removeItem("pending_checkout"); } catch {}
    if (!["trial", "monthly"].includes(pending)) return;
    (async () => {
      try {
        const { url } = await createCheckout(pending);
        window.location.href = url;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not resume checkout");
      }
    })();
  }, [user]);

  const trial = packages.find(p => p.id === "trial");
  const monthly = packages.find(p => p.id === "monthly");

  return (
    <div className="relative z-10 min-h-screen" data-testid="pricing-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <Link to="/" className="font-serif-display text-2xl" data-testid="back-home">SafeRxCheck</Link>
        <Link to="/" className="label-small">← Back</Link>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-10 sm:pb-12">
        <p className="label-small mb-4">PRICING · NO LOCK-IN</p>
        <h1 className="font-serif-display text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
            style={{ color: "var(--text-primary)" }}>
          Start small. Stay informed.
        </h1>
        <p className="mt-5 sm:mt-6 max-w-2xl text-[0.95rem] sm:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Try SafeRxCheck for 5 days for the price of a coffee. Continue with a monthly plan that brings
          gentle, personalised reminders directly to your inbox.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {/* Trial */}
        <div className="rounded-3xl border p-6 sm:p-10 lift"
             style={{ background: "white", borderColor: "var(--border-default)" }}
             data-testid="trial-card">
          <p className="label-small">01 · Try it</p>
          <h2 className="font-serif-display text-3xl mt-2" style={{ color: "var(--text-primary)" }}>5-day trial</h2>
          <p className="mt-4 font-serif-display text-4xl sm:text-5xl" style={{ color: "var(--brand)" }}>
            ${trial?.amount?.toFixed(2) ?? "5.00"}<span className="text-base font-outfit" style={{ color: "var(--text-muted)" }}> · one-time</span>
          </p>
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            Full access for 5 days. No automatic renewal — re-subscribe whenever you like.
          </p>
          <button
            onClick={() => buy("trial")}
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 min-h-[52px] transition-all duration-300 disabled:opacity-50"
            style={{ background: "transparent", color: "var(--brand)", border: "1px solid var(--brand)" }}
            data-testid="buy-trial-btn"
          >
            Start 5-day trial <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Monthly */}
        <div className="relative rounded-3xl border p-6 sm:p-10 lift"
             style={{ background: "var(--brand)", borderColor: "var(--brand)" }}
             data-testid="monthly-card">
          <span className="absolute top-5 right-5 sm:top-6 sm:right-6 text-xs rounded-full px-3 py-1"
                style={{ background: "var(--terracotta)", color: "white" }}>POPULAR</span>
          <p className="label-small" style={{ color: "#A3B19B" }}>02 · Stay supported</p>
          <h2 className="font-serif-display text-3xl mt-2" style={{ color: "white" }}>Monthly</h2>
          <p className="mt-4 font-serif-display text-4xl sm:text-5xl" style={{ color: "white" }}>
            ${monthly?.amount?.toFixed(2) ?? "30.00"}<span className="text-base font-outfit" style={{ color: "#A3B19B" }}> / 30 days</span>
          </p>
          <ul className="mt-6 space-y-2.5">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed" style={{ color: "white" }} data-testid={`feat-${i}`}>
                <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "#A3B19B" }} />
                {f}
              </li>
            ))}
          </ul>
          <button
            onClick={() => buy("monthly")}
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 min-h-[52px] transition-all duration-300 disabled:opacity-50"
            style={{ background: "white", color: "var(--brand)" }}
            data-testid="buy-monthly-btn"
          >
            <Sparkles className="h-4 w-4" /> Subscribe — $30/month
          </button>
          {!user && (
            <p className="mt-4 text-xs text-center" style={{ color: "#A3B19B" }}>
              You&apos;ll sign in or sign up first.
            </p>
          )}
        </div>
      </section>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode="signup" />
    </div>
  );
}
